// src/pages/ReportsPage.js
import React, { useEffect, useMemo, useState } from 'react';
import BackToDashboard from '../components/BackToDashboard';
import './ReportsPage.css';
import API_BASE from '../apiConfig';

/** Normalisera roll → är transportör? */
const isTransportorByRole = (role) => {
  const r = String(role || '').toLowerCase().trim();
  // normRole i servern ger t.ex. "admin" eller "transportör"
  return r === 'transportör' || r === 'transportor' || r.includes('transport');
};

/** Normalisera email (lowercase, trim) */
const normEmail = (v) => String(v || '').trim().toLowerCase();

/** Visa datum snyggt */
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('sv-SE') : '—');

/** Datum för sortering / visning */
const getCreatedAtFromDoc = (doc = {}) =>
  doc.created_at ||
  (doc.payload && (doc.payload.createdAt || doc.payload.pickupDate)) ||
  doc.pickupDate ||
  null;

/**
 * Försök lista ut vilket transportör-konto ett dokument hör till.
 * Vi använder bara emails som finns i transportorEmailSet.
 */
const getTransportEmailFromDoc = (doc = {}, transportorEmailSet) => {
  const p = doc.payload || {};

  // 1) Nytt fält från backend: _ownerUserEmail
  const ownerEmail = normEmail(
    p._ownerUserEmail ||
      p.createdByEmail ||
      doc.created_by // om du i framtiden börjar sätta det
  );
  if (ownerEmail && transportorEmailSet.has(ownerEmail)) {
    return ownerEmail;
  }

  // 2) Fallback: kolla email-fält i payload, men bara om de matchar en transportör
  const candidates = [
    p.email,
    p.contactEmail,
    p.uppgiftsEmail,
  ];

  for (const c of candidates) {
    const e = normEmail(c);
    if (e && transportorEmailSet.has(e)) {
      return e;
    }
  }

  // Ingen match -> vi räknar inte dokumentet på någon transportör
  return '';
};

export default function ReportsPage() {
  const [users, setUsers] = useState([]);
  const [transports, setTransports] = useState([]);
  const [sourceNote, setSourceNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selectedEmail, setSelectedEmail] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setErr('');
      setSourceNote('');

      try {
        // 1) Hämta inloggad användare + companyId
        const meRes = await fetch(`${API_BASE}/auth/me`, {
          credentials: 'include',
        });
        if (!meRes.ok) throw new Error(`HTTP ${meRes.status}`);
        const me = await meRes.json();
        const companyId = me?.company?.id || me?.user?.company_id;
        if (!companyId) throw new Error('Hittar inget företag (companyId) på kontot.');

        // 2) Hämta användare + dokument parallellt
        const [usersRes, docsRes] = await Promise.all([
          fetch(`${API_BASE}/users?companyId=${companyId}`, {
            credentials: 'include',
          }),
          fetch(`${API_BASE}/documents`, {
            credentials: 'include',
          }),
        ]);

        if (!usersRes.ok) throw new Error(`HTTP ${usersRes.status}`);
        if (!docsRes.ok) throw new Error(`HTTP ${docsRes.status}`);

        const usersJson = await usersRes.json();
        const docsJson = await docsRes.json();

        if (cancelled) return;

        setUsers((usersJson || []).map((u) => ({ ...u, _src: 'api' })));
        setTransports(Array.isArray(docsJson) ? docsJson : []);
        setSourceNote('Data från backend (users + documents).');

        // Debug-loggar en gång vid laddning:
        console.log('[REPORTS] users:', usersJson);
        console.log(
          '[REPORTS] documents (id -> payload.email, _ownerUserEmail):',
          (docsJson || []).map((d) => ({
            id: d.id,
            email: d?.payload?.email || null,
            _ownerUserEmail: d?.payload?._ownerUserEmail || null,
          }))
        );
      } catch (e) {
        if (!cancelled) {
          setErr(e.message || 'Kunde inte hämta data från backend.');
          setUsers([]);
          setTransports([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Alla transportör-konton
  const transportors = useMemo(
    () => users.filter((u) => isTransportorByRole(u.role)),
    [users]
  );

  // Set med transportörernas normaliserade emails
  const transportorEmailSet = useMemo(() => {
    const s = new Set();
    transportors.forEach((u) => {
      const e = normEmail(u.email);
      if (e) s.add(e);
    });
    return s;
  }, [transportors]);

  // Förberäkna antal dokument per transportör-email
  const transportCounts = useMemo(() => {
    const counts = {};
    transports.forEach((doc) => {
      const key = getTransportEmailFromDoc(doc, transportorEmailSet);
      if (!key) return; // dokumentet tillhör ingen transportör vi visar
      counts[key] = (counts[key] || 0) + 1;
    });
    console.log('[REPORTS] per-user counts:', counts);
    return counts;
  }, [transports, transportorEmailSet]);

  // Detaljlistan längst ner
  const transportsForSelected = useMemo(() => {
    if (!selectedEmail) return [];
    const selKey = normEmail(selectedEmail);
    if (!selKey) return [];

    return transports
      .filter(
        (d) => getTransportEmailFromDoc(d, transportorEmailSet) === selKey
      )
      .sort(
        (a, b) =>
          new Date(getCreatedAtFromDoc(b) || 0) -
          new Date(getCreatedAtFromDoc(a) || 0)
      );
  }, [transports, selectedEmail, transportorEmailSet]);

  return (
    <div className="reports-dashboard">
      <div
        style={{
          marginBottom: '1.2rem',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <BackToDashboard label="← Tillbaka till Dashboard" />
      </div>

      <h2 className="reports-title">Rapporter</h2>

      <section className="card">
        <div className="card-head">
          <h3 className="card-title">Transportörer</h3>
          <div className="card-count">{transportors.length}</div>
        </div>

        {loading ? (
          <div className="muted">Laddar data…</div>
        ) : err ? (
          <div className="error">Fel: {err}</div>
        ) : transportors.length === 0 ? (
          <>
            <div className="muted">Inga transportörer hittades.</div>
            {sourceNote && (
              <div className="muted" style={{ marginTop: 6 }}>
                {sourceNote}
              </div>
            )}
          </>
        ) : (
          <>
            {sourceNote && (
              <div className="muted" style={{ marginBottom: 8 }}>
                {sourceNote}
              </div>
            )}

            <div className="transportor-list">
              {transportors.map((u) => {
                const userEmailLower = normEmail(u.email);
                const count = transportCounts[userEmailLower] || 0;
                const active =
                  normEmail(selectedEmail) === userEmailLower;

                return (
                  <button
                    key={`${u._src || 'api'}-${u.id}-${u.email}`}
                    className={`transportor-row${active ? ' is-active' : ''}`}
                    onClick={() => setSelectedEmail(u.email)}
                    title={`Visa skickade dokument från ${u.email}`}
                  >
                    <div className="transportor-name">{u.email}</div>
                    <div className="transportor-meta">
                      {count} skickad{count === 1 ? '' : 'e'}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </section>

      {selectedEmail && (
        <section className="card" style={{ marginTop: 16 }}>
          <div className="card-head">
            <h3 className="card-title">
              Skickade dokument –{' '}
              <span style={{ fontWeight: 600 }}>{selectedEmail}</span>
            </h3>
            <div className="card-count">{transportsForSelected.length}</div>
          </div>

          {transportsForSelected.length === 0 ? (
            <div className="muted">Inga transporter har skickats ännu.</div>
          ) : (
            <div className="reports-list">
              {transportsForSelected.map((d) => {
                const p = d.payload || {};
                const date = getCreatedAtFromDoc(d);
                const waste = d.wasteTypes || p.wasteTypes || '—';
                const amount = p.amount ?? '—';
                const unit = p.unit || 'kg';

                return (
                  <div key={d.id} className="report-item">
                    <span className="item-date">{fmtDate(date)}</span>
                    <span className="item-creator">{waste}</span>
                    <span className="item-amount">
                      {amount} {unit}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
